import { useApp } from '@/App';
import { useUserData } from '@/libs/fleet-api/hooks';
import {
	calculateBusinessProfit,
	calculateEBITDA,
	calculateEquity,
	calculateGrossProfitAndLoss,
	calculateProfitLossBeforeTax,
	calculateProfitLossCurrentYear,
	calculateTotalAsset,
	calculateTotalCurrentAsset,
	calculateTotalLiability,
	calculateTotalLongTermLiability,
	calculateTotalNonCurrentAsset,
	calculateTotalShortTermLiability,
} from '@/libs/antd/calculation';
import FleetModal, { useAsyncModal } from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import { inputCurrency } from '@/libs/antd/input-number';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import rule from '@/libs/antd/rules';
import {
	useSelectFinancialReportAuditStatus,
	useSelectFinancialReportPeriodType,
	useSelectFinancialReportPeriodTypeDetail,
} from '@/libs/fleet-api/hooks/master-data';
import { SubmitMPPFinancialReport } from '@/libs/fleet-api/services/mpp';
import {
	FileData,
	ProspectFinancialReport,
} from '@/libs/fleet-api/services/types';
import { uploader } from '@/libs/fleet-api/uploader';
import { LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import {
	Button,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Select,
	Space,
	Tabs,
	Upload,
	UploadFile,
} from 'antd';
import dayjs from 'dayjs';
import { FC, Fragment, useState } from 'react';

export type FinancialReportModalForm = SubmitMPPFinancialReport;

export type FinancialReportModalParam =
	| {
			action: 'add';
			customerId?: string;
			prospectId: string;
	  }
	| {
			action: 'edit';
			record: FinancialReportModalForm;
			customerId?: string;
			prospectId: string;
	  }
	| {
			action: 'view';
			record: FinancialReportModalForm;
			customerId?: string;
			prospectId: string;
	  };

export const useFinancialReportModal = () => {
	return useAsyncModal<FinancialReportModalParam, FinancialReportModalForm>();
};

const FinancialReportModal: FC<ReturnType<typeof useFinancialReportModal>> = ({
	modal,
	close,
	data,
}) => {
	const { notification } = useApp();
	const { fieldName, form, value } = useForm<SubmitMPPFinancialReport>();
	const [activeTab, setActiveTab] = useState('Neraca');

	const { groupName } = useUserData() ?? {};
	const isReadOnly = data?.action === 'view';
	const isViewOnly = isReadOnly || (groupName !== 'FRM' && groupName !== 'CCA');

	const selectFinancialReportAuditStatus =
		useSelectFinancialReportAuditStatus();

	const selectFinancialReportPeriodType = useSelectFinancialReportPeriodType();

	// Get periodTypeId from either form value or initial data (for edit mode)
	const periodTypeId =
		value?.periodTypeId ||
		(data?.action !== 'add' ? data?.record?.periodTypeId : undefined);

	const selectFinancialReportPeriodTypeDetail =
		useSelectFinancialReportPeriodTypeDetail(
			periodTypeId
				? {
						parentDataId: periodTypeId,
						parentSlug: 'financial-report-period-type',
					}
				: undefined,
			true,
		);

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	return (
		<FleetModal
			{...modal}
			width={940}
			keyboard={false}
			buttonPosition="end"
			styles={{
				footer: {
					display: 'none',
				},
			}}
			afterOpenChange={(isOpen) => {
				if (isOpen && data?.action === 'edit') {
					form.setFieldsValue(data.record);
				}

				if (!isOpen) form.resetFields();
			}}
		>
			<div style={{ marginBottom: 20 }}>
				<FleetTitle>
					{data?.action === 'edit' ? 'Edit' : 'Add'} Laporan Keuangan
				</FleetTitle>
			</div>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				disabled={isViewOnly}
				onValuesChange={(changedValues, allValues) => {
					// NERACA
					if (changedValues.balanceSheet || changedValues.profitLoss) {
						const formData = allValues as SubmitMPPFinancialReport;

						if (formData.balanceSheet || formData.profitLoss) {
							// Create a temporary object that matches the calculation function's expected structure
							const calculationData = {
								balanceSheet: formData.balanceSheet || {},
								profitAndLoss: formData.profitLoss || {}, // Note: using profitAndLoss for calculations but profitLoss for form
							};

							// Calculate balance sheet totals
							const totalCurrentAsset = calculateTotalCurrentAsset(
								calculationData as unknown as ProspectFinancialReport<'payload'>,
							);
							const totalNonCurrentAsset = calculateTotalNonCurrentAsset(
								calculationData as unknown as ProspectFinancialReport<'payload'>,
							);
							const totalShortTermLiability = calculateTotalShortTermLiability(
								calculationData as unknown as ProspectFinancialReport<'payload'>,
							);
							const totalLongTermLiability = calculateTotalLongTermLiability(
								calculationData as unknown as ProspectFinancialReport<'payload'>,
							);
							const totalLiability = calculateTotalLiability(
								calculationData as unknown as ProspectFinancialReport<'payload'>,
							);
							const totalAsset = calculateTotalAsset(
								calculationData as unknown as ProspectFinancialReport<'payload'>,
							);
							const equity = calculateEquity(
								calculationData as unknown as ProspectFinancialReport<'payload'>,
							);
							const totalLiabilityAndEquity = equity + totalLiability;

							// Calculate profit and loss totals
							const grossProfitLoss = calculateGrossProfitAndLoss(
								calculationData as unknown as ProspectFinancialReport<'payload'>,
							);
							const ebitda = calculateEBITDA(
								calculationData as unknown as ProspectFinancialReport<'payload'>,
							);
							const businessProfit = calculateBusinessProfit(
								calculationData as unknown as ProspectFinancialReport<'payload'>,
							);
							const profitLossBeforeTax = calculateProfitLossBeforeTax(
								calculationData as unknown as ProspectFinancialReport<'payload'>,
							);
							const profitLossTheYear = calculateProfitLossCurrentYear(
								calculationData as unknown as ProspectFinancialReport<'payload'>,
							);

							// Update form with calculated values
							const updatedValues = {
								...formData,
							};

							// Only update balance sheet if it exists and has changes
							if (formData.balanceSheet && changedValues.balanceSheet) {
								updatedValues.balanceSheet = {
									...formData.balanceSheet,
									totalCurrentAsset,
									totalNonCurrentAsset,
									totalShortTermLiability,
									totalLongTermLiability,
									totalAsset,
									totalLiability,
									equity,
									totalLiabilityAndEquity,
								};
							}

							// Only update profit loss if it exists and has changes
							if (formData.profitLoss && changedValues.profitLoss) {
								updatedValues.profitLoss = {
									...formData.profitLoss,
									grossProfitLoss,
									ebitda,
									businessProfit,
									profitLossBeforeTax,
									profitLossTheYear,
								};
							}

							// Use setTimeout to avoid React state update conflicts
							setTimeout(() => {
								form.setFieldsValue(updatedValues);
							}, 0);
						}
					}
				}}
			>
				<Form.Item
					name={fieldName('id')}
					noStyle
				/>
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							label="Laporan Keuangan"
							name={fieldName('auditStatusId')}
							labelCol={{ span: 10 }}
							rules={isViewOnly ? [] : [rule.required()]}
						>
							<Select
								{...selectFinancialReportAuditStatus}
								disabled={isViewOnly}
							/>
						</Form.Item>
					</Col>
					{value?.auditStatusId === 1 && (
						<Col span={12}>
							<Form.Item
								label="Nama Auditor"
								name={fieldName('auditorName')}
								labelCol={{ span: 10 }}
								rules={isViewOnly ? [] : [rule.required()]}
							>
								<Input disabled={isViewOnly} />
							</Form.Item>
						</Col>
					)}
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							label="Periode"
							name={fieldName('periodTypeId')}
							labelCol={{ span: 10 }}
							rules={isViewOnly ? [] : [rule.required()]}
							getValueFromEvent={(_, option) => option?.value}
							getValueProps={(value) => {
								if (!value) return {};
								// Find the option that matches the stored ID
								const option = selectFinancialReportPeriodType.options?.find(
									(opt) => opt.value === value,
								);
								return {
									value: option || value,
								};
							}}
						>
							<Select
								{...selectFinancialReportPeriodType}
								disabled={isViewOnly}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							label="Detail Periode"
							name={fieldName('periodTypeDetailId')}
							labelCol={{ span: 10 }}
							rules={isViewOnly ? [] : [rule.required()]}
							getValueFromEvent={(_, option) => option?.value}
							getValueProps={(value) => {
								if (!value) return {};

								// Find the option that matches the stored ID
								const option =
									selectFinancialReportPeriodTypeDetail.options?.find(
										(opt) => opt.value === value,
									);

								// In view-only mode, return the label text instead of option object
								// so it displays the readable text instead of the ID
								if (isViewOnly && option) {
									return {
										value: option.label,
									};
								}

								return {
									value: option || value,
								};
							}}
						>
							<Select
								{...selectFinancialReportPeriodTypeDetail}
								disabled={isViewOnly}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							label="Tahun"
							name={fieldName('year')}
							labelCol={{ span: 10 }}
							getValueProps={(value) => ({
								value: value && dayjs().set('year', value),
							})}
							normalize={(value) =>
								value && Number(dayjs(value).format('YYYY'))
							}
							rules={isViewOnly ? [] : [rule.required()]}
						>
							<DatePicker.YearPicker disabled={isViewOnly} />
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 20]}>
					<Col span={12}>
						<Form.Item
							name={fieldName('financialReportFile')}
							label="Upload Laporan Keuangan"
							labelCol={{ span: 10 }}
							rules={isViewOnly ? [] : [rule.required()]}
							getValueProps={(value) => {
								const file = value as FileData;
								return {
									fileList: file?.id
										? ([
												{
													uid: file.id,
													name: file.name,
													thumbUrl: file.url,
												},
											] as UploadFile[])
										: [],
								};
							}}
							normalize={({ file }: { file: UploadFile }) => {
								return {
									id: file.uid,
									format: file.type,
									name: file.name,
									url: file.thumbUrl,
								} as FileData;
							}}
						>
							<Upload.Dragger
								listType="picture"
								accept=".jpg,.jpeg,.png"
								maxCount={1}
								beforeUpload={() => false}
								disabled={isViewOnly}
								onRemove={() => {
									form.setFieldValue(fieldName('financialReportFile'), null);
									return false;
								}}
								onChange={async ({ file }) => {
									const fileid = Date.now().toString();
									const filepath = `customer/${data!.customerId}/${data!.prospectId}/financial-reports`;

									try {
										const url = await uploadMutation.mutateAsync({
											file: file as unknown as File,
											fileid,
											filepath,
										});
										form.setFieldValue(fieldName('financialReportFile'), {
											format: 'jpeg',
											id: fileid,
											name: file.name,
											path: filepath,
											url,
										});
									} catch {
										form.setFieldValue(fieldName('financialReportFile'), null);
									}
								}}
							>
								{uploadMutation.isPending ? (
									<LoadingOutlined
										style={{
											fontSize: 25,
											color: '#BABABA',
											display: 'block',
										}}
									/>
								) : (
									<UploadOutlined
										style={{
											fontSize: 25,
											color: '#BABABA',
											display: 'block',
										}}
									/>
								)}
							</Upload.Dragger>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Tabs
					activeKey={activeTab}
					onChange={(key) => setActiveTab(key)}
					items={[
						{
							key: 'Neraca',
							label: 'Neraca',
							children: (
								<Fragment>
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['balanceSheet', 'cash'])}
												label="Kas Dan Setara Kas"
												labelCol={{ span: 10 }}
												rules={isViewOnly ? [] : [rule.required()]}
											>
												<InputNumber
													{...inputCurrency}
													disabled={isViewOnly}
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName(['balanceSheet', 'shortTermLoan'])}
												label="Pinjaman Jangka Pendek"
												labelCol={{ span: 10 }}
												rules={isViewOnly ? [] : [rule.required()]}
											>
												<InputNumber
													{...inputCurrency}
													disabled={isViewOnly}
												/>
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName([
													'balanceSheet',
													'financingReceivableCurrentAsset',
												])}
												label="Piutang Usaha Pembiayaan Aset Lancar"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<InputNumber {...inputCurrency} />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName([
													'balanceSheet',
													'shortTermAccountPayable',
												])}
												label="Utang Usaha Jangka Pendek"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<InputNumber {...inputCurrency} />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName([
													'balanceSheet',
													'otherFinancialAssetInvestmentCurrentAsset',
												])}
												label="Investasi Aset Keuangan Lainnya Aset Lancar"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<InputNumber {...inputCurrency} />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName([
													'balanceSheet',
													'otherShortTermLiability',
												])}
												label="Liabilitas Jangka Pendek Lainnya"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<InputNumber {...inputCurrency} />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['balanceSheet', 'otherCurrentAsset'])}
												label="Aset Lancar Lainnya"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<InputNumber {...inputCurrency} />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName([
													'balanceSheet',
													'totalShortTermLiability',
												])}
												label="Total Liabilitas Jangka Pendek"
												labelCol={{ span: 10 }}
											>
												<InputNumber
													{...inputCurrency}
													disabled
												/>
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['balanceSheet', 'totalCurrentAsset'])}
												label="Total Aset Lancar"
												labelCol={{ span: 10 }}
											>
												<InputNumber
													{...inputCurrency}
													disabled
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName(['balanceSheet', 'longTermLoan'])}
												label="Pinjaman Jangka Panjang"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<InputNumber {...inputCurrency} />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName([
													'balanceSheet',
													'financingReceivableNonCurrentAsset',
												])}
												label="Piutang Usaha Pembiayaan Aset Tidak Lancar"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<InputNumber {...inputCurrency} />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName([
													'balanceSheet',
													'longTermAccountPayable',
												])}
												label="Utang Usaha Jangka Panjang"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<InputNumber {...inputCurrency} />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName([
													'balanceSheet',
													'otherFinancialAssetInvestmentNonCurrentAsset',
												])}
												label="Investasi Aset Keuangan Lainnnya Aset Tidak Lancar"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<InputNumber {...inputCurrency} />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName([
													'balanceSheet',
													'otherLongTermLiability',
												])}
												label="Liabilitas Jangka Panjang Lainnya"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<InputNumber {...inputCurrency} />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName([
													'balanceSheet',
													'otherNonCurrentAsset',
												])}
												label="Aset Tidak Lancar Lainnya"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<InputNumber {...inputCurrency} />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName([
													'balanceSheet',
													'totalLongTermLiability',
												])}
												label="Total Liabilitas Jangka Panjang"
												labelCol={{ span: 10 }}
											>
												<InputNumber
													{...inputCurrency}
													disabled
												/>
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName([
													'balanceSheet',
													'totalNonCurrentAsset',
												])}
												label="Total Aset Tidak Lancar"
												labelCol={{ span: 10 }}
											>
												<InputNumber
													{...inputCurrency}
													disabled
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName(['balanceSheet', 'equity'])}
												label="Ekuitas"
												labelCol={{ span: 10 }}
											>
												<InputNumber
													{...inputCurrency}
													disabled
												/>
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['balanceSheet', 'totalAsset'])}
												label={
													<span style={{ color: '#1557BC' }}>TOTAL ASET</span>
												}
												labelCol={{
													span: 10,
													style: {
														color: '#1557BC',
													},
												}}
											>
												<InputNumber
													{...inputCurrency}
													disabled
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName(['balanceSheet', 'totalLiability'])}
												label={
													<span style={{ color: '#1557BC' }}>
														TOTAL LIABILITAS
													</span>
												}
												labelCol={{ span: 10 }}
											>
												<InputNumber
													{...inputCurrency}
													disabled
												/>
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}></Col>
										<Col span={12}>
											<Form.Item
												name={fieldName([
													'balanceSheet',
													'totalLiabilityAndEquity',
												])}
												label={
													<span style={{ color: '#1557BC' }}>
														TOTAL LIABILITAS & EKUITAS
													</span>
												}
												labelCol={{ span: 10 }}
											>
												<InputNumber
													{...inputCurrency}
													disabled
												/>
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Space
										style={{
											display: 'flex',
											justifyContent: 'flex-end',
										}}
										size="large"
									>
										<Button
											style={{
												width: 200,
												display: 'block',
											}}
											onClick={() => close()}
										>
											CANCEL
										</Button>
										<Button
											type="primary"
											style={{
												width: 200,
												display: 'block',
											}}
											onClick={async () => {
												try {
													await form.validateFields(
														[fieldName('balanceSheet')],
														{
															recursive: true,
														},
													);
													setActiveTab('Laba Rugi');
												} catch {
													notification.error({
														message: 'Validasi Gagal',
														description:
															'Belum semua field diisi, mohon periksa kembali dan lengkapi field yang Wajib diisi',
													});
												}
											}}
										>
											NEXT
										</Button>
									</Space>
								</Fragment>
							),
						},
						{
							key: 'Laba Rugi',
							label: 'Laba Rugi',
							children: (
								<Fragment>
									<Form.Item
										name={fieldName(['profitLoss', 'operatingBusinessIncome'])}
										label="Pendapatan Usaha Operasional"
										labelCol={{ span: 12 }}
										rules={[rule.required()]}
									>
										<InputNumber {...inputCurrency} />
									</Form.Item>
									<Divider />
									<Form.Item
										name={fieldName(['profitLoss', 'costOfRevenue'])}
										label="Beban Pokok Pendapatan"
										labelCol={{ span: 12 }}
										rules={[rule.required()]}
									>
										<InputNumber {...inputCurrency} />
									</Form.Item>
									<Divider />
									<Form.Item
										name={fieldName(['profitLoss', 'grossProfitLoss'])}
										label="Laba Rugi Bruto"
										labelCol={{ span: 12 }}
									>
										<InputNumber
											{...inputCurrency}
											disabled
										/>
									</Form.Item>
									<Divider />
									<Form.Item
										name={fieldName(['profitLoss', 'costOfMarketingAndAdmin'])}
										label="Biaya Pemasaran Umum dan Administrasi"
										labelCol={{ span: 12 }}
										rules={[rule.required()]}
									>
										<InputNumber {...inputCurrency} />
									</Form.Item>
									<Divider />
									<Form.Item
										name={fieldName(['profitLoss', 'ebitda'])}
										label="EBITDA"
										labelCol={{ span: 12 }}
									>
										<InputNumber
											{...inputCurrency}
											disabled
										/>
									</Form.Item>
									<Divider />
									<Form.Item
										name={fieldName([
											'profitLoss',
											'costOfDepreciationAndAmortization',
										])}
										label="Biaya Depresiasi dan Amortisasi"
										labelCol={{ span: 12 }}
										rules={[rule.required()]}
									>
										<InputNumber {...inputCurrency} />
									</Form.Item>
									<Divider />
									<Form.Item
										name={fieldName(['profitLoss', 'businessProfit'])}
										label="Laba Usaha"
										labelCol={{ span: 12 }}
									>
										<InputNumber
											{...inputCurrency}
											disabled
										/>
									</Form.Item>

									<Divider />

									<Form.Item
										name={fieldName(['profitLoss', 'otherIncome'])}
										label="Pendapatan Lain-Lain"
										labelCol={{ span: 12 }}
										rules={[rule.required()]}
									>
										<InputNumber {...inputCurrency} />
									</Form.Item>

									<Divider />

									<Form.Item
										name={fieldName(['profitLoss', 'otherExpense'])}
										label="Beban Lain-Lain"
										labelCol={{ span: 12 }}
										rules={[rule.required()]}
									>
										<InputNumber {...inputCurrency} />
									</Form.Item>
									<Divider />
									<Form.Item
										name={fieldName(['profitLoss', 'profitLossBeforeTax'])}
										label="Laba Rugi Sebelum Pajak"
										labelCol={{ span: 12 }}
									>
										<InputNumber
											{...inputCurrency}
											disabled
										/>
									</Form.Item>
									<Divider />
									<Form.Item
										name={fieldName(['profitLoss', 'taxBenefitOrCost'])}
										label="Manfaat atau Beban Pajak"
										labelCol={{ span: 12 }}
										rules={[rule.required()]}
									>
										<InputNumber {...inputCurrency} />
									</Form.Item>
									<Divider />

									<Form.Item
										name={fieldName(['profitLoss', 'profitLossTheYear'])}
										label="Laba Rugi Tahun Berjalan"
										labelCol={{ span: 12 }}
									>
										<InputNumber
											{...inputCurrency}
											disabled
										/>
									</Form.Item>
									<Divider />

									<Space
										style={{
											display: 'flex',
											justifyContent: 'flex-end',
										}}
										size="large"
										onClick={() => close()}
									>
										<Button
											style={{
												width: 200,
												display: 'block',
											}}
										>
											CANCEL
										</Button>
										<Button
											type="primary"
											style={{
												width: 200,
												display: 'block',
												marginLeft: 'auto',
											}}
											onClick={async () => {
												if (isViewOnly) {
													close();
													return;
												}
												try {
													const payload = await form.validateFields();
													close(payload);
												} catch {
													notification.error({
														message: 'Validasi Gagal',
														description:
															'Belum semua field diisi, mohon periksa kembali dan lengkapi field yang Wajib diisi',
													});
												}
											}}
										>
											{isViewOnly ? 'CLOSE' : 'SAVE'}
										</Button>
									</Space>
								</Fragment>
							),
						},
					]}
				/>
			</Form>
		</FleetModal>
	);
};

export default FinancialReportModal;
